/**
 * Hook for saving filter profiles to database
 */

import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useSafeNavigate } from '@/hooks/useSafeNavigate';
import { toast } from 'sonner';
import type { CatalogFiltersState } from './useCatalogFilters';
import { logger } from '@/lib/services';

/**
 * Generate a human-readable name from filters
 */
function generateFilterName(filters: CatalogFiltersState): string {
  const parts: string[] = [];
  
  if (filters.speciesKey === 'dog') parts.push('Собаки');
  else if (filters.speciesKey === 'cat') parts.push('Кошки');
  
  if (filters.breedIds.length > 0) {
    const breedName = Object.values(filters.breedNames)[0];
    if (breedName) parts.push(breedName);
  }
  
  if (filters.geoPlaceName) parts.push(filters.geoPlaceName);
  
  if (filters.sex.length === 1) {
    parts.push(filters.sex[0] === 'male' ? 'мальчики' : 'девочки');
  }
  
  return parts.length > 0 ? parts.join(', ') : 'Мой поиск';
}

export function useSaveFilterProfile() {
  const { user } = useAuth();
  const navigate = useSafeNavigate();
  const [isSaving, setIsSaving] = useState(false);

  const saveFilters = useCallback(async (
    filters: CatalogFiltersState,
    name?: string,
    notifyEnabled: boolean = false
  ): Promise<string | null> => {
    if (!user) {
      toast.error('Войдите, чтобы сохранить поиск');
      return null;
    }

    setIsSaving(true);
    try {
      // Build filters payload for JSONB storage
      const filtersPayload = {
        speciesKey: filters.speciesKey,
        breedIds: filters.breedIds,
        breedNames: filters.breedNames,
        sex: filters.sex,
        ageBuckets: filters.ageBuckets,
        geoPlaceId: filters.geoPlaceId,
        geoPlaceName: filters.geoPlaceName,
        sizes: filters.sizes,
        colors: filters.colors,
        healthSpecialNeeds: filters.healthSpecialNeeds,
        onlyWithPhotos: filters.onlyWithPhotos,
        sort: filters.sort,
      };

      const profileName = name || generateFilterName(filters);

      const { data, error } = await supabase.rpc('fn_user_filter_profile_upsert_v1', {
        p_profile_id: null,
        p_user_id: user.id,
        p_name: profileName,
        p_description: null,
        p_filters: filtersPayload,
        p_is_ai: false,
        p_ai_label: null,
        p_ai_model_version: null,
        p_ai_explanation: null,
        p_set_active: false,
      });

      if (error) throw error;

      // Update notify_enabled separately since RPC may not support it yet
      if (data && notifyEnabled) {
        const client = supabase as unknown as { 
          from: (table: string) => { 
            update: (d: Record<string, unknown>) => { 
              eq: (col: string, val: string) => Promise<{ error: Error | null }> 
            } 
          } 
        };
        await client
          .from('user_filter_profiles')
          .update({ notify_enabled: notifyEnabled })
          .eq('id', data as string);
      }

      const notifyMessage = notifyEnabled 
        ? 'Поиск сохранён! Мы уведомим о новых питомцах' 
        : 'Поиск сохранён!';

      toast.success(notifyMessage, {
        action: {
          label: 'Перейти',
          onClick: () => navigate('/favorites?tab=searches'),
        },
      });

      logger.info('Filter profile saved', {
        context: { feature: 'catalog', function_name: 'saveFilters' },
      });

      return data as string;
    } catch (err) {
      logger.error('Failed to save filter profile', {
        context: { feature: 'catalog', function_name: 'saveFilters' },
        error: err as Error,
        error_code: 'save_filter_failed',
        error_type: 'unexpected',
      });
      toast.error('Не удалось сохранить поиск');
      return null;
    } finally {
      setIsSaving(false);
    }
  }, [user, navigate]);

  return { saveFilters, isSaving };
}
